import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UseUserCreationProps {
  onSuccess: () => void;
}

interface CreateUserParams {
  email: string;
  password: string;
  fullName: string;
}

export const useUserCreation = ({ onSuccess }: UseUserCreationProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const createUser = async ({ email, password, fullName }: CreateUserParams) => {
    setIsLoading(true);
    
    try {
      // Get current user
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        toast.error("Vous devez être connecté pour ajouter un utilisateur");
        return false;
      }

      console.log("Creating user with reference to owner:", currentUser.id);

      // Create the user with metadata
      const { data: newUser, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            user_type: 'owner_user',
            reference_owner_id: currentUser.id
          }
        }
      });

      if (signUpError) {
        console.error('Error creating user:', signUpError);
        if (signUpError.message.includes('User already registered')) {
          toast.error("Un utilisateur existe déjà avec cet email");
        } else {
          toast.error("Erreur lors de la création de l'utilisateur: " + signUpError.message);
        }
        return false;
      }

      if (!newUser.user) {
        toast.error("Erreur lors de la création de l'utilisateur");
        return false;
      }

      console.log("User created successfully:", newUser.user.id);
      console.log("Creating owner_users association...");

      // Create owner_user association
      const { error: linkError } = await supabase
        .from('owner_users')
        .insert({
          owner_id: currentUser.id,
          user_id: newUser.user.id,
          hidden_features: []
        });

      if (linkError) {
        console.error('Error linking user:', linkError);
        toast.error("Erreur lors de l'association de l'utilisateur");
        return false;
      }

      console.log("Owner_users association created successfully");
      toast.success("Utilisateur ajouté avec succès!");
      toast.info(`Email: ${email}\nMot de passe: ${password}`);
      onSuccess();
      return true;
    } catch (error: any) {
      console.error('Error adding user:', error);
      toast.error(error.message || "Erreur lors de l'ajout de l'utilisateur");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createUser,
    isLoading,
  };
};